import React, { useState, useEffect } from "react"
import {
  UseFormRegister,
  FieldErrors,
  Path,
  UseFormWatch,
  UseFormSetValue,
} from "react-hook-form"
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react"
import { CalendarIcon } from "@heroicons/react/24/outline"
import { useTranslation } from "react-i18next"

import { Calendar } from "components/ui/calendar"
import { useDropdownPosition } from "hooks/utils/use-dropdown-position"
import { getLocalizedText } from "utils/i18n/get-localized-text"
import { PlaceOrderFormData, Question } from "types"
import {
  parseFormattedDate,
  formatDateForPayload as formatPayload,
  formatDateForDisplay,
  isValidDateString,
} from "utils/date-formatter"

interface DateQuestionProps {
  question: Question
  register: UseFormRegister<PlaceOrderFormData>
  errors: FieldErrors<PlaceOrderFormData>
  watch: UseFormWatch<PlaceOrderFormData>
  setValue: UseFormSetValue<PlaceOrderFormData>
  ticketId: number
}

const DateQuestion: React.FC<DateQuestionProps> = ({
  question,
  register,
  errors,
  watch,
  setValue,
  ticketId,
}) => {
  const { t } = useTranslation()
  const [selectedDate, setSelectedDate] = useState<Date | undefined>()

  // Use dropdown position hook with transparent styling for calendar
  const { buttonRef, getDropdownClassName } = useDropdownPosition({
    itemCount: 8, // Approximate number of calendar rows
    itemHeight: 40, // Approximate height of each calendar row
    extraPadding: 24, // Extra padding for calendar borders and spacing
    mobileThreshold: 0.5,
    spaceBuffer: 100,
    isTransparent: true,
  })

  const fieldName =
    `tickets.${ticketId}.${question.uuid}` as Path<PlaceOrderFormData>
  const fieldError = errors.tickets?.[ticketId]?.[question.uuid]

  const formValue = watch(fieldName)

  useEffect(() => {
    if (formValue && typeof formValue === "string") {
      try {
        // Try to parse with our custom format first
        const date = parseFormattedDate(
          formValue,
          question.date_format || "month_day_year"
        )

        if (date) {
          setSelectedDate(date)
        } else {
          // Fallback for other formats (like existing ISO strings)
          const fallbackDate = new Date(formValue)
          if (!isNaN(fallbackDate.getTime())) {
            setSelectedDate(fallbackDate)
          }
        }
      } catch {
        // Invalid date string, ignore
      }
    } else if (!formValue) {
      setSelectedDate(undefined)
    }
  }, [formValue, question.date_format])

  const questionLabel = getLocalizedText(question.text, "Question")

  const formatDateForPayload = (date: Date): string => {
    return formatPayload(date, question.date_format || "month_day_year")
  }

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date)
      const formattedDate = formatDateForPayload(date)
      setValue(fieldName, formattedDate, { shouldValidate: true })
    }
  }

  const formatDate = (date: Date | undefined): string => {
    if (!date) return t("validation.select_date", "Select date")
    return formatDateForDisplay(date, question.date_format || "month_day_year")
  }

  // Calculate year range (100 years in past, 100 years in future)
  const currentYear = new Date().getFullYear()
  const startMonth = new Date(currentYear - 100, 0) // January, 100 years ago
  const endMonth = new Date(currentYear + 100, 11) // December, 100 years from now

  return (
    <div>
      <h4 className="block mb-3 text-sm font-medium text-theme_text">
        {questionLabel}
        {question.requires_answer && (
          <span className="ml-1 text-red-500">*</span>
        )}
      </h4>

      <div className="relative">
        <Popover>
          {({ open, close }) => (
            <>
              <PopoverButton
                ref={buttonRef}
                className={`
                  relative w-full rounded-md py-2 pl-3 pr-3 text-left
                  border transition-colors duration-200 text-sm group
                  ${
                    fieldError
                      ? "text-red-900 border-red-300 focus:border-red-500 focus:ring-red-500 cursor-default bg-white"
                      : `text-theme_text border-gray-300 cursor-default bg-white ${
                          open
                            ? "border-theme_accent ring-2 ring-theme_accent ring-offset-2"
                            : "hover:border-theme_text_medium focus:border-theme_accent focus:ring-2 focus:ring-theme_accent focus:ring-offset-2"
                        }`
                  }
                  focus:outline-none
                `}
              >
                <span className="flex justify-between items-center w-full">
                  <span
                    className={
                      !selectedDate ? "text-gray-400" : "text-theme_text"
                    }
                  >
                    {formatDate(selectedDate)}
                  </span>
                  <CalendarIcon
                    className={`w-5 h-5 ml-2 flex-shrink-0 transition-colors duration-200 ${
                      open ? "text-theme_accent" : "text-theme_text_medium"
                    } group-focus:text-theme_accent`}
                  />
                </span>
              </PopoverButton>

              <PopoverPanel className={getDropdownClassName(open)}>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    handleDateSelect(date)
                    if (date) {
                      // Close the popover after selection
                      setTimeout(() => close(), 150)
                    }
                  }}
                  defaultMonth={selectedDate}
                  className="rounded-md border shadow-lg"
                  captionLayout="dropdown"
                  startMonth={startMonth}
                  endMonth={endMonth}
                />
              </PopoverPanel>
            </>
          )}
        </Popover>
      </div>

      <input
        type="hidden"
        {...register(fieldName, {
          required: question.requires_answer
            ? t("validation.field_required", "This field is required")
            : false,
          validate: (value) => {
            const dateValue = value as string

            // Empty value handling
            if (!dateValue) {
              return question.requires_answer
                ? t("validation.date_required", "Please select a date")
                : true
            }

            if (
              isValidDateString(
                dateValue,
                question.date_format || "month_day_year"
              )
            ) {
              return true
            }

            return t("validation.date_invalid", "Please select a valid date")
          },
        })}
      />

      {fieldError && (
        <p
          className="mt-2 text-sm text-red-500"
          role="alert"
          id={`${question.uuid}-error`}
        >
          {fieldError.message}
        </p>
      )}
    </div>
  )
}

export { DateQuestion }
